/**
 * Inject citation markers into an MRR entry's markdown, for `ReactMarkdown`
 * with `rehypeRaw` to render.
 *
 * Mirrors how search highlighting works in this component (`searchHighlight.ts`
 * injects `<mark>` into the same string): the markdown stays the source of
 * truth and the marker is added as raw HTML the renderer maps to a React
 * component.
 *
 * A citation anchors by its stored `summaryText` occurring **exactly once** in
 * the entry. That is the contract the writer enforces — it resolves the model's
 * span to a literal markdown slice before storing — so a plain `indexOf` is the
 * whole lookup, with no normalization step here.
 */

/** One stored `SummaryCitation`, reduced to what rendering needs. */
export interface SummaryCitationAnchor {
  /** `SummaryCitation.id` — what a remove acts on. */
  id: string;
  /** The claim as cited, verbatim markdown. */
  summaryText: string;
  /** `Citation.id` — the source block, resolved to a page/bbox on demand. */
  citationID: string;
}

export const CITATION_MARKER_ATTR = "data-citation-ids";

/**
 * One claim's citations, located in the text being rendered.
 *
 * `endsAt` is where the marker goes: after the claim, in whatever space the
 * caller searched — markdown offsets for the display renderer, plain-text
 * offsets for the editor.
 */
export interface AnchoredCitationGroup {
  /**
   * The claim's anchor text — what identifies the group, and stable across a
   * source being removed from it, which `ids` is not. The editor keys a
   * marker's host element by this, so a key that changed on removal would
   * strand the host and blank the markers still on the claim.
   */
  key: string;
  /** `SummaryCitation.id`s sharing this anchor, rendered as `[1][2][3]`. */
  ids: string[];
  endsAt: number;
  /** Length of the claim as matched, so a caller can recover its start. */
  length: number;
}

export interface AnchoredCitations {
  /** In reading order, so a caller can insert back-to-front safely. */
  groups: AnchoredCitationGroup[];
  /**
   * `SummaryCitation.id` → the number rendered for it. Derived from where the
   * anchors actually land, and only for citations that anchored — so numbering
   * reads 1,2,3 down the entry with no gaps, whatever order the rows arrived
   * in and however many were skipped.
   */
  numbers: Map<string, number>;
}

/**
 * Locate every citation in `text`, dropping the ones that won't anchor.
 *
 * The single resolution step behind both renderers. Display mode searches raw
 * markdown; the editor searches its own plain-text content and passes
 * `toSearchSpace` to strip the markers out of the stored anchor first. Sharing
 * it is what keeps a claim's number the same in both modes — the alternative
 * is two `indexOf` loops that agree until one of them is touched.
 *
 * Anchors that are absent or repeated are skipped: a reader searching for them
 * would find zero or several places and could not say which one the claim
 * meant. Silently rendering nothing is the safe direction — a missing marker is
 * visible, a marker on the wrong sentence is not.
 */
/**
 * The `[1][2][3]` sets: citations sharing an anchor are the links on one claim.
 *
 * Split out from resolution so a caller that needs the groups but not their
 * positions — the editor, which hosts one marker element per group — cannot
 * end up asking the question a second, subtly different way.
 */
export const citationGroups = (
  citations: readonly SummaryCitationAnchor[],
): { key: string; ids: string[] }[] => {
  const byAnchor = new Map<string, string[]>();
  for (const citation of citations) {
    const existing = byAnchor.get(citation.summaryText);
    if (existing) existing.push(citation.id);
    else byAnchor.set(citation.summaryText, [citation.id]);
  }
  return [...byAnchor].map(([anchor, ids]) => ({ key: anchor, ids }));
};

export const anchorCitations = (
  text: string,
  citations: readonly SummaryCitationAnchor[],
  toSearchSpace: (anchor: string) => string = (anchor) => anchor,
): AnchoredCitations => {
  const groups: AnchoredCitationGroup[] = [];
  for (const { key: anchor, ids } of citationGroups(citations)) {
    const needle = toSearchSpace(anchor);
    if (!needle) continue;
    const at = text.indexOf(needle);
    if (at === -1) continue;
    if (text.indexOf(needle, at + 1) !== -1) continue;
    groups.push({
      key: anchor,
      ids,
      endsAt: at + needle.length,
      length: needle.length,
    });
  }

  // Number by position in the text, not by the order the rows arrived in.
  groups.sort((a, b) => a.endsAt - b.endsAt);
  const numbers = new Map<string, number>();
  let next = 1;
  for (const group of groups) {
    for (const id of group.ids) {
      numbers.set(id, next);
      next += 1;
    }
  }
  return { groups, numbers };
};

/** The markdown with markers inserted, plus the numbering that matches it. */
export interface CitationMarkup {
  content: string;
  numbers: Map<string, number>;
}

/**
 * Insert a marker after every anchored claim.
 *
 * The editor does not use this: its content is the save payload, so an injected
 * tag would be written back into the summary. It renders the same markers as
 * ProseMirror decorations instead — see `CitationDecorationExtension`.
 */
export const injectCitationMarkers = (
  content: string,
  citations: readonly SummaryCitationAnchor[],
): CitationMarkup => {
  const { groups, numbers } = anchorCitations(content, citations);

  // Apply back-to-front so each offset stays valid against the original string.
  let result = content;
  for (const group of [...groups].reverse()) {
    result = `${result.slice(0, group.endsAt)}<sup ${CITATION_MARKER_ATTR}="${group.ids.join(",")}"></sup>${result.slice(group.endsAt)}`;
  }
  return { content: result, numbers };
};
